import { HDate, HebrewCalendar, Location, GeoLocation, Zmanim, flags } from '@hebcal/core';

// ---------- Types (same shapes previously provided by src/mocks) ----------

export interface ZmanimDay {
  date: string;
  alotHashachar: string;
  netz: string;
  sofZmanShemaMga: string;
  sofZmanShemaGra: string;
  sofZmanTefilla: string;
  chatzot: string;
  minchaGedola: string;
  minchaKetana: string;
  plagHaMincha: string;
  shkia: string;
  tzet: string;
}

export const zmanimLabels: Record<string, string> = {
  alotHashachar: 'Alot HaShachar',
  netz: 'Netz (Sunrise)',
  sofZmanShemaMga: 'Sof Zman Shema (MGA)',
  sofZmanShemaGra: 'Sof Zman Shema (GRA)',
  sofZmanTefilla: 'Sof Zman Tefilla',
  chatzot: 'Chatzot (Midday)',
  minchaGedola: 'Mincha Gedola',
  minchaKetana: 'Mincha Ketana',
  plagHaMincha: 'Plag HaMincha',
  shkia: 'Shkia (Sunset)',
  tzet: 'Tzet HaKochavim',
};

export interface CalendarEvent {
  date: string;
  hebrewDate: string;
  isShabbat: boolean;
  isYomTov: boolean;
  isRoshChodesh: boolean;
  holidayName: string | null;
  parsha: string | null;
  candleLighting: string | null;
  havdalah: string | null;
  fastName: string | null;
  fastBegins: string | null;
  fastEnds: string | null;
}

export interface GeoOptions {
  latitude: number;
  longitude: number;
  elevation?: number;
  timezone: string;
  alotMethod?: AlotMethod;
  tzetMethod?: TzetMethod;
}

export const DEFAULT_GEO: GeoOptions = {
  latitude: 40.7128,
  longitude: -74.006,
  elevation: 10,
  timezone: 'America/New_York',
};

// ---------- Zman calculation methods ----------

export type AlotMethod = '72min' | '90min' | '120min' | '16.1deg';
export type TzetMethod = '72min' | '50min' | '8.5deg' | '7.083deg' | '6.45deg';

/** Selectable Alot HaShachar opinions (value → human label). */
export const alotMethods: { value: AlotMethod; label: string }[] = [
  { value: '72min', label: '72 minutes before sunrise' },
  { value: '90min', label: '90 minutes before sunrise' },
  { value: '120min', label: '120 minutes before sunrise' },
  { value: '16.1deg', label: '16.1° (degrees below horizon)' },
];

/** Selectable Tzet HaKochavim opinions (value → human label). */
export const tzetMethods: { value: TzetMethod; label: string }[] = [
  { value: '72min', label: '72 minutes after sunset (Rabbeinu Tam)' },
  { value: '50min', label: '50 minutes after sunset' },
  { value: '8.5deg', label: '8.5° (3 medium stars)' },
  { value: '7.083deg', label: '7.083° (3 stars)' },
  { value: '6.45deg', label: '6.45° (early)' },
];

// ---------- Helpers ----------

function formatTime(d: Date | null, tzid: string): string {
  if (!d || isNaN(d.getTime())) return '--:--';
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tzid,
  }).format(d);
}

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Get a Date object representing "now" — kept as a function for testability. */
export function today(): Date {
  return new Date();
}

// ---------- Zmanim ----------

/** Alot HaShachar for the chosen opinion (default: 72 fixed minutes before sunrise). */
export function computeAlot(z: Zmanim, method: AlotMethod = '72min'): Date {
  switch (method) {
    case '90min': return new Date(z.sunrise().getTime() - 90 * 60000);
    case '120min': return new Date(z.sunrise().getTime() - 120 * 60000);
    case '16.1deg': return z.alotHaShachar();
    case '72min':
    default: return new Date(z.sunrise().getTime() - 72 * 60000);
  }
}

/** Tzet HaKochavim for the chosen opinion (default: 72 fixed minutes after sunset). */
export function computeTzet(z: Zmanim, method: TzetMethod = '72min'): Date {
  switch (method) {
    case '50min': return new Date(z.shkiah().getTime() + 50 * 60000);
    case '8.5deg': return z.tzeit(8.5);
    case '7.083deg': return z.tzeit(7.083);
    case '6.45deg': return z.tzeit(6.45);
    case '72min':
    default: return new Date(z.shkiah().getTime() + 72 * 60000);
  }
}

/** Compute all daily zmanim for a location. Pure computation — no network. */
export function computeZmanim(date: Date, geo: GeoOptions = DEFAULT_GEO): ZmanimDay {
  const gloc = new GeoLocation(null, geo.latitude, geo.longitude, geo.elevation ?? 0, geo.timezone);
  const z = new Zmanim(gloc, date, Boolean(geo.elevation));
  const tz = geo.timezone;
  return {
    date: toDateStr(date),
    alotHashachar: formatTime(computeAlot(z, geo.alotMethod), tz),
    netz: formatTime(z.sunrise(), tz),
    sofZmanShemaMga: formatTime(z.sofZmanShmaMGA(), tz),
    sofZmanShemaGra: formatTime(z.sofZmanShma(), tz),
    sofZmanTefilla: formatTime(z.sofZmanTfilla(), tz),
    chatzot: formatTime(z.chatzot(), tz),
    minchaGedola: formatTime(z.minchaGedola(), tz),
    minchaKetana: formatTime(z.minchaKetana(), tz),
    plagHaMincha: formatTime(z.plagHaMincha(), tz),
    shkia: formatTime(z.shkiah(), tz),
    tzet: formatTime(computeTzet(z, geo.tzetMethod), tz),
  };
}

/** Raw zman Date objects (for countdowns / scheduling math). */
export function computeZmanimDates(date: Date, geo: GeoOptions = DEFAULT_GEO): Record<string, Date> {
  const gloc = new GeoLocation(null, geo.latitude, geo.longitude, geo.elevation ?? 0, geo.timezone);
  const z = new Zmanim(gloc, date, Boolean(geo.elevation));
  return {
    alotHashachar: computeAlot(z, geo.alotMethod),
    netz: z.sunrise(),
    sofZmanShemaMga: z.sofZmanShmaMGA(),
    sofZmanShemaGra: z.sofZmanShma(),
    sofZmanTefilla: z.sofZmanTfilla(),
    chatzot: z.chatzot(),
    minchaGedola: z.minchaGedola(),
    minchaKetana: z.minchaKetana(),
    plagHaMincha: z.plagHaMincha(),
    shkia: z.shkiah(),
    tzet: computeTzet(z, geo.tzetMethod),
  };
}

// ---------- Hebrew calendar events ----------

export function hebrewDateString(d: Date): string {
  const hd = new HDate(d);
  return `${hd.getDate()} ${hd.getMonthName()} ${hd.getFullYear()}`;
}

/**
 * Compute calendar events (Hebrew dates, Shabbat, holidays, parsha,
 * candle lighting/havdalah) for a Gregorian month. Pure local computation.
 * month is 0-based (JS convention).
 */
export function computeMonthEvents(
  year: number,
  month: number,
  geo: GeoOptions = DEFAULT_GEO,
): Record<string, CalendarEvent> {
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  const il = geo.timezone === 'Asia/Jerusalem';
  const location = new Location(
    geo.latitude, geo.longitude, il, geo.timezone, undefined, undefined, undefined, geo.elevation,
  );

  let events: ReturnType<typeof HebrewCalendar.calendar> = [];
  try {
    events = HebrewCalendar.calendar({
      start,
      end,
      location,
      candlelighting: true,
      sedrot: true,
      il,
    });
  } catch {
    // If candle-lighting computation fails (bad coords), fall back to basic calendar
    events = HebrewCalendar.calendar({ start, end, sedrot: true, il });
  }

  const result: Record<string, CalendarEvent> = {};

  // Base entry for every day of the month
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const key = toDateStr(d);
    result[key] = {
      date: key,
      hebrewDate: hebrewDateString(d),
      isShabbat: d.getDay() === 6,
      isYomTov: false,
      isRoshChodesh: false,
      holidayName: null,
      parsha: null,
      candleLighting: null,
      havdalah: null,
      fastName: null,
      fastBegins: null,
      fastEnds: null,
    };
  }

  for (const ev of events) {
    const d = ev.getDate().greg();
    const key = toDateStr(d);
    const entry = result[key];
    if (!entry) continue;

    // Timed events (candle lighting / havdalah / fast begins / fast ends).
    // These are handled by their description; skip the flag-based logic below so
    // that hebcal's own "Fast begins: 3:41am" text never leaks into holidayName.
    // We compute our own fast begin/end times using the user's Alot/Tzet opinions.
    const desc = ev.getDesc();
    const timed = ev as unknown as { eventTime?: Date };
    if (desc === 'Candle lighting') {
      if (timed.eventTime) entry.candleLighting = formatTime(timed.eventTime, geo.timezone);
      continue;
    }
    if (desc === 'Havdalah') {
      if (timed.eventTime) entry.havdalah = formatTime(timed.eventTime, geo.timezone);
      continue;
    }
    if (desc === 'Fast begins' || desc === 'Fast ends') {
      continue;
    }

    const f = ev.getFlags();
    if (f & flags.PARSHA_HASHAVUA) {
      entry.parsha = ev.render('en').replace(/^Parashat\s+/i, '');
    } else if (f & flags.ROSH_CHODESH) {
      entry.isRoshChodesh = true;
      entry.holidayName = ev.render('en');
    } else if (f & flags.CHAG) {
      entry.isYomTov = true;
      entry.holidayName = ev.render('en');
    } else if (f & (flags.MAJOR_FAST | flags.MINOR_FAST | flags.MINOR_HOLIDAY | flags.MODERN_HOLIDAY)) {
      entry.holidayName = entry.holidayName || ev.render('en');
    }

    // Fast days: compute begin/end using the user's chosen Alot/Tzet opinions
    // (hebcal's own "Fast begins" uses a fixed 16.1°, which we intentionally override).
    if (f & (flags.MAJOR_FAST | flags.MINOR_FAST)) {
      entry.fastName = ev.render('en');
      const gloc = new GeoLocation(null, geo.latitude, geo.longitude, geo.elevation ?? 0, geo.timezone);
      const zDay = new Zmanim(gloc, d, Boolean(geo.elevation));
      if (f & flags.MAJOR_FAST) {
        // Yom Kippur / Tish'a B'Av begin at sunset the previous evening.
        const prev = new Date(d);
        prev.setDate(prev.getDate() - 1);
        const zPrev = new Zmanim(gloc, prev, Boolean(geo.elevation));
        entry.fastBegins = formatTime(zPrev.shkiah(), geo.timezone);
      } else {
        // Minor fasts begin at dawn (Alot HaShachar).
        entry.fastBegins = formatTime(computeAlot(zDay, geo.alotMethod), geo.timezone);
      }
      entry.fastEnds = formatTime(computeTzet(zDay, geo.tzetMethod), geo.timezone);
    }
  }

  return result;
}

/** Compute the calendar event for a single date. */
export function computeDayEvent(date: Date, geo: GeoOptions = DEFAULT_GEO): CalendarEvent {
  const events = computeMonthEvents(date.getFullYear(), date.getMonth(), geo);
  return events[toDateStr(date)];
}

export { toDateStr };
